import { formatDateTimeForDisplay, parseDateFromValue } from "../FilterBuilderRow/value-utils";

import { FilterValueType, type FilterEnumOption } from "./structured-search-schema";
import type { FilterValue, PromptFilterValue } from "./types";

const COST_FRACTION_DIGITS = 6;
const ANY_VERSION_SUFFIX = "(Any version)";
const RANGE_SEPARATOR = " - ";

export interface ValueFormatterArgs {
  type: FilterValueType;
  value: FilterValue;
  operator: string;
  options?: FilterEnumOption[];
  /** Pre-resolved labels for multi-value filters (e.g. hydrated prompt names). */
  multiValueDisplay?: string[];
}

const isPromptFilterValue = (value: unknown): value is PromptFilterValue =>
  typeof value === "object" &&
  value !== null &&
  "prompt_id" in value &&
  typeof (value as PromptFilterValue).prompt_id === "number";

const formatPromptValue = (value: PromptFilterValue): string =>
  value.prompt_version_number !== undefined
    ? `Prompt #${value.prompt_id} v${value.prompt_version_number}`
    : `Prompt #${value.prompt_id} ${ANY_VERSION_SUFFIX}`;

const formatEnumValue = (value: string, options?: FilterEnumOption[]): string =>
  options?.find(option => option.value === value)?.label ?? value;

const formatCostValue = (value: number): string => `$${value.toFixed(COST_FRACTION_DIGITS)}`;

const formatDateValue = (value: string): string => {
  const parsed = parseDateFromValue(value);
  return parsed ? formatDateTimeForDisplay(parsed) : value;
};

const formatScalar = (
  type: FilterValueType,
  value: FilterValue,
  options?: FilterEnumOption[],
): string => {
  switch (type) {
    case FilterValueType.Prompt:
      return isPromptFilterValue(value) ? formatPromptValue(value) : String(value);
    case FilterValueType.Enum:
      return typeof value === "string" ? formatEnumValue(value, options) : String(value);
    case FilterValueType.Cost:
      return typeof value === "number" ? formatCostValue(value) : String(value);
    case FilterValueType.Date:
      return typeof value === "string" ? formatDateValue(value) : String(value);
    case FilterValueType.Boolean:
      return "";
    default:
      return String(value);
  }
};

const formatArray = ({
  type,
  value,
  operator,
  options,
  multiValueDisplay,
}: ValueFormatterArgs): string => {
  const values = value as FilterValue[];

  if (type === FilterValueType.Prompt) {
    const labels =
      multiValueDisplay && multiValueDisplay.length > 0
        ? multiValueDisplay
        : values.map(item =>
            isPromptFilterValue(item) ? formatPromptValue(item) : `Prompt #${String(item)}`,
          );
    return `(${labels.join(", ")})`;
  }

  if (type === FilterValueType.DateRange && values.length >= 2) {
    return `${formatDateValue(String(values[0] ?? ""))}${RANGE_SEPARATOR}${formatDateValue(
      String(values[1] ?? ""),
    )}`;
  }

  if (values.length === 2 && values.every(item => typeof item === "number")) {
    if (type === FilterValueType.Cost) {
      return `${formatCostValue(values[0] as number)}${RANGE_SEPARATOR}${formatCostValue(
        values[1] as number,
      )}`;
    }
    return `${values[0]}${RANGE_SEPARATOR}${values[1]}`;
  }

  if (operator === "in" || operator === "not_in") {
    const labels =
      multiValueDisplay && multiValueDisplay.length > 0
        ? multiValueDisplay
        : values.map(item => formatScalar(type, item as FilterValue, options)).filter(Boolean);
    const joined = labels.join(", ");
    return joined ? `(${joined})` : "";
  }

  return values.map(item => formatScalar(type, item as FilterValue, options)).join(", ");
};

/**
 * Centralized, type-driven value formatter. Formats the value portion of a
 * chip / selected value by {@link FilterValueType} plus selected option labels,
 * replacing the per-filter `formatValue` callbacks.
 */
export const formatFilterValueByType = (args: ValueFormatterArgs): string => {
  const { type, value } = args;

  if (value === true || value === false) {
    return "";
  }

  if (Array.isArray(value)) {
    return formatArray(args);
  }

  if (typeof value === "object" && value !== null && "amount" in value) {
    return `${value.amount} ${value.unit}`;
  }

  return formatScalar(type, value, args.options);
};
